/**
 * Stand in for a web storage the browser doesn't provide.
 *
 * An Android app that embeds a WebView with DOM storage switched off
 * (`setDomStorageEnabled(false)`, the WebView default) exposes
 * `window.localStorage` and `window.sessionStorage` as `null`. This app reads
 * both bare from its stores, router, views and components, so the first read
 * at boot threw and the signup page never came up (DABBLE-ACCOUNTS-2-50,
 * DABBLE-ACCOUNTS-2-4Z). Some browsers throw on access instead (sandboxed
 * iframes, storage disabled by policy).
 *
 * Replacing a missing store with an in-memory one lets the app run for the
 * life of the page: sign-in and checkout work, the visitor just isn't
 * remembered across loads, which that browser can't do anyway. A store that
 * works is never replaced. Installed first in `main.ts`, before anything reads
 * storage.
 */
type StorageName = 'localStorage' | 'sessionStorage';

const STORAGE_NAMES: StorageName[] = ['localStorage', 'sessionStorage'];

export class MemoryStorage implements Storage {
  private items = new Map<string, string>();

  get length(): number {
    return this.items.size;
  }

  key(index: number): string | null {
    return [...this.items.keys()][index] ?? null;
  }

  getItem(key: string): string | null {
    return this.items.get(String(key)) ?? null;
  }

  setItem(key: string, value: string): void {
    this.items.set(String(key), String(value));
  }

  removeItem(key: string): void {
    this.items.delete(String(key));
  }

  clear(): void {
    this.items.clear();
  }
}

function isUsable(win: Window, name: StorageName): boolean {
  try {
    const storage = win[name];
    if (!storage) return false;
    storage.getItem('');
    return true;
  } catch {
    return false;
  }
}

/** Returns the names of the stores it replaced. */
export function installStorageFallback(win: Window = window): StorageName[] {
  const replaced: StorageName[] = [];
  for (const name of STORAGE_NAMES) {
    if (isUsable(win, name)) continue;
    try {
      Object.defineProperty(win, name, { value: new MemoryStorage(), configurable: true });
      replaced.push(name);
    } catch {
      // Not redefinable here: leave the browser's own behaviour in place.
    }
  }
  return replaced;
}
